// Componente assegnazione tecnico al ticket
import { useQuery } from '@tanstack/react-query'
import { User } from 'lucide-react'
import type { Ticket } from '@fieldcore/types'
import { Select } from '../../../components/ui/Select'
import { Button } from '../../../components/ui/Button'
import { Avatar } from '../../../components/ui/Avatar'
import { useAssignTicket } from '../../../hooks/useTickets'
import api from '../../../lib/api'
import { useState } from 'react'

interface Technician {
  id: string
  name?: string
  firstName?: string
  lastName?: string
  email?: string
  role: string
  available?: boolean
  isActive?: boolean
}

interface TicketAssignmentProps {
  ticket: Ticket
}

export function TicketAssignment({ ticket }: TicketAssignmentProps) {
  const [selectedId, setSelectedId] = useState<string>('')
  const assignMutation = useAssignTicket(ticket.id)

  const { data: technicians = [] } = useQuery({
    queryKey: ['technicians', 'available'],
    queryFn: async () => {
      try {
        // /technicians/available ritorna { userId, fullName, isAvailable, ... }
        const res = await api.get<Array<{ userId: string; fullName: string; isAvailable: boolean }>>('/technicians/available')
        const items = res.data ?? []
        return items.map((t) => ({
          id: t.userId,
          name: t.fullName,
          role: 'technician',
          available: t.isAvailable,
        })) as Technician[]
      } catch {
        try {
          // Fallback: lista completa /technicians
          const res = await api.get<Technician[]>('/technicians')
          return (res.data ?? []).filter((t) => Boolean(t.id))
        } catch {
          return [] as Technician[]
        }
      }
    },
  })

  function getTechName(t: Technician): string {
    if (t.name) return t.name
    if (t.firstName || t.lastName) return `${t.firstName ?? ''} ${t.lastName ?? ''}`.trim()
    return t.email ?? (t.id ? t.id.slice(0, 8) : '—')
  }

  const options = technicians
    .filter((t) => Boolean(t.id))
    .map((t) => ({
      value: t.id,
      label: getTechName(t),
      disabled: false,
    }))

  // Nome tecnico correntemente assegnato (cerca nella lista caricata)
  const assignedTech = ticket.assignedTo
    ? technicians.find((t) => t.id === ticket.assignedTo)
    : null
  const assignedName = assignedTech
    ? getTechName(assignedTech)
    : ticket.assignedTo
      ? 'Tecnico assegnato'
      : null

  return (
    <div className="space-y-4">
      {assignedName ? (
        <div className="flex items-center gap-3 rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface-raised)] p-3">
          <Avatar name={assignedName} size="md" online />
          <div>
            <p className="text-sm font-semibold text-[var(--fc-foreground)]">{assignedName}</p>
            <p className="text-xs text-[var(--fc-foreground-muted)]">Tecnico assegnato</p>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2 text-sm text-[var(--fc-foreground-muted)]">
          <User className="h-4 w-4" aria-hidden="true" />
          <span>Ticket non assegnato</span>
        </div>
      )}

      <div className="flex gap-2">
        <div className="flex-1">
          <Select
            options={options}
            value={selectedId}
            onValueChange={setSelectedId}
            placeholder="Seleziona tecnico..."
          />
        </div>
        <Button
          onClick={() => {
            if (!selectedId) return
            assignMutation.mutate(selectedId, {
              onSuccess: () => setSelectedId(''),
            })
          }}
          disabled={!selectedId}
          isLoading={assignMutation.isPending}
          size="md"
        >
          Assegna
        </Button>
      </div>
    </div>
  )
}
